<template>
  <section id="seminar">
    <div class="seminarTitle" v-if="isArchived">
      <HeadingLv2 text="セミナー紹介" bgBlue />
    </div>
    <template v-else>
      <h2 class="benefits_heading">
        基調講演・特別セミナーに参加して、研修ノウハウをもっと知ろう！
      </h2>
      <Checklist
        :items="[
          'セミナーはイベント予約フォームから申込みください。',
          '申込みの際は注意事項をよくご確認の上、申込みください。'
        ]"
      />
    </template>

    <div>
      <SeminarCassette
        v-for="item in seminars"
        :key="item"
        :heading="item.title"
        class="seminarArchive"
      >
        <div>
          <p>{{ item.description }}</p>
          <template v-if="!isArchived">
            <h4>セミナー概要</h4>
            <dt>申込方法</dt>
            <dd v-html="item.howto"></dd>
            <dt>申し込み開始日</dt>
            <dd>{{ getDateToTime(item.term.start) }}〜</dd>
            <dt>申し込み終了日</dt>
            <dd>{{ getDateToTime(item.term.end) }}まで</dd>
            <div v-if="isFinish(item)" class="note">
              申込み終了となりました。当日はお席が空いておりましたら受講いただけますので、ご希望の方は直接会場までお越しください。
            </div>
            <dt>定員</dt>
            <dd v-html="item.capacity"></dd>
            <dt>開催時間</dt>
            <dd v-html="item.time"></dd>
          </template>
          <HeadingLv3 text="講師紹介" class="lecturerTitle" />
          <div
            class="lecturerWrapper"
            v-if="item.lecturer && item.lecturer.length"
          >
            <SeminarLecturer
              v-for="(lecturer, i) in item.lecturer"
              :key="i"
              :lecturer="lecturer"
              class="lecturer"
            />
          </div>
          <div
            class="buttonWrapper"
            v-if="
              isArchived &&
                item.movie &&
                item.movie?.filter((m) => m.label.includes('video')).length
            "
          >
            <Button
              label="視聴する"
              primary
              @click="openModal(item.movie, item.title)"
            />
          </div>
        </div>
      </SeminarCassette>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/runtime-core'
import Button from '@/components/elements/Button.vue'
import HeadingLv2 from '@/components/elements/HeadingLv2.vue'
import HeadingLv3 from '@/components/elements/HeadingLv3.vue'
import Checklist from '@/components/block/Checklist.vue'
import SeminarCassette from '@/components/block/SeminarCassette.vue'
import SeminarLecturer from '@/components/block/SeminarLecturer.vue'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
dayjs.locale('ja')

interface Movie {
  type: string
  id: string
  thumb: string
  label: string[]
  title: string
  description: string
}
interface Seminar {
  title: string
  finish: boolean
  term: {
    start: string
    end: string
  }
  description: string
  howto: string
  capacity: string
  time: string
  lecturer: any[]
  movie: Movie[]
  program_id: string
}

export default defineComponent({
  name: 'EventDetailSeminars',

  components: {
    Button,
    HeadingLv2,
    HeadingLv3,
    SeminarLecturer,
    Checklist,
    SeminarCassette
  },

  props: {
    seminars: {
      type: Array as PropType<Seminar[]>,
      default: () => []
    },
    isArchived: {
      type: Boolean,
      required: true
    },
    openModal: {
      type: Function as PropType<(movies: any, title: string) => void>
    }
  },

  setup() {
    const getDateToTime = (date: string) =>
      dayjs(date).format('YYYY年M月D日（ddd）H時mm分')

    const isFinish = (seminar: Seminar) => {
      const isExpired = dayjs().isAfter(dayjs(seminar.term.end))
      return seminar.finish || isExpired
    }

    return {
      getDateToTime,
      isFinish
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.seminarTitle {
  margin-top: 40px;
}

.benefits_heading {
  margin-top: 42px;
  margin-bottom: 13px;
  font-size: 2.2rem;
  line-height: 1.5;
  font-weight: 500;
}

::v-deep(.checklist) {
  margin-bottom: 24px;
}

.seminarArchive {
  margin-top: 16px !important;
  .lecturerTitle {
    margin-top: 28px;
    @include mixins.mediaMax() {
      margin-bottom: -12px;
    }
  }
  .lecturer {
    &:not(:last-child) {
      @include mixins.mediaMax() {
        border-bottom: solid 0.5px var.$color-border-gray;
      }
    }
    padding: 24px 0 18px;
    @include mixins.media() {
      padding: 12px 0;
    }
  }

  .lecturerWrapper {
    display: grid;
    grid-template-columns: repeat(auto-fill, calc(100%));
    @include mixins.media() {
      grid-template-columns: repeat(auto-fill, calc(50% - 14px));
    }
    gap: 0px 28px;
  }
  .buttonWrapper {
    border-top: solid 0.5px var.$color-border-gray;
    margin: 12px -18px -20px;
    padding: 24px 0;
  }
}
</style>
